import React, { memo, useCallback, useRef, useState } from 'react';
import {
  ArrowShapeKindStyle,
  DefaultStylePanel,
  DefaultStylePanelContent,
  TldrawUiButton,
  TldrawUiButtonIcon,
  useEditor,
  usePassThroughWheelEvents,
  useRelevantStyles,
  useTranslation,
  useValue,
} from 'tldraw';
import { readBoardStylePanelState } from '../utils/boardStylePanelState';

/**
 * Style panel in the top-right of the board.
 * Shown only while a styled tool or selection is active (text, connectors, geo,
 * notes, draw, highlight, line). Hidden entirely otherwise — no compact control.
 * A manual close lasts until the tool or selection changes.
 */
const BoardStylePanel = memo(function BoardStylePanel({ isMobile }) {
  const editor = useEditor();
  const styles = useRelevantStyles();
  const msg = useTranslation();
  const collapsedRef = useRef(null);
  usePassThroughWheelEvents(collapsedRef);
  const panelKey = useValue(
    'board-style-panel',
    () => readBoardStylePanelState(editor).key,
    [editor]
  );
  const [pinned, setPinned] = useState(null);
  const autoExpand = panelKey.startsWith('1|');
  const manualOpen = pinned && pinned.key === panelKey ? pinned.open : null;
  const expanded = isMobile || (manualOpen == null ? autoExpand : manualOpen);

  const toggleCollapsed = useCallback(() => {
    setPinned({ key: panelKey, open: !expanded });
  }, [expanded, panelKey]);

  if (!styles || !autoExpand) return null;

  // Connecting lines are always edge-aligned curves. Hide arc/elbow.
  const panelStyles = {
    get(style) {
      if (style === ArrowShapeKindStyle) return undefined;
      return styles.get(style);
    },
  };

  const title = msg('style-panel.title');
  const isCollapsed = !expanded;

  if (isCollapsed) {
    return (
      <div
        ref={collapsedRef}
        className="tlui-style-panel tlui-style-panel__wrapper board-style-panel board-style-panel--collapsed"
        data-ismobile={isMobile}
        data-expanded="false"
      >
        <TldrawUiButton
          type="icon"
          title={title}
          data-testid="style-panel.toggle-expand"
          onClick={toggleCollapsed}
          className="board-style-panel__toggle"
          aria-expanded="false"
          aria-label={title}
        >
          <span className="board-style-panel__toggle-label">{title}</span>
          <TldrawUiButtonIcon small icon="chevron-down" />
        </TldrawUiButton>
      </div>
    );
  }

  return (
    <DefaultStylePanel isMobile={isMobile}>
      {!isMobile ? (
        <button
          type="button"
          className="board-style-panel__collapse"
          title={title}
          data-testid="style-panel.toggle-collapse"
          onClick={toggleCollapsed}
          aria-expanded="true"
          aria-label={title}
        >
          <span className="board-style-panel__collapse-label">{title}</span>
          <TldrawUiButtonIcon small icon="chevron-up" />
        </button>
      ) : null}
      <DefaultStylePanelContent styles={panelStyles} />
    </DefaultStylePanel>
  );
});

export default BoardStylePanel;
